import { useEffect } from 'react';

import { css, Global } from '@emotion/react';
import { ToucanComponents, ToucanColors } from '@jointoucan/toucan-design';
import { Box, useTheme } from '@mui/material';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import Layout from '~/components/shared/Layout';
import { Events } from '~/constants/events';
import STORAGE from '~/constants/storage';
import { trackEvent } from '~/helpers/analytics';
import { useExtension } from '~/hooks/useExtension';
import useLocalStorage from '~/hooks/useLocalStorage';
import { useLoggedInUser } from '~/hooks/useLoggedInUser';
import { useRedirectToStore } from '~/hooks/useRedirectToStore';
import { useToast } from '~/hooks/useToast';
import { getTranslations } from '~/lib/next-i18next';

const { Typography, Button, useColorScheme } = ToucanComponents;

export const DownloadPrompt = () => {
  const theme = useTheme();
  const { t } = useTranslation('download-prompt');
  const { textLevel1 } = useColorScheme();
  const { isInstalled, loading: isExtensionStatusLoading } = useExtension();
  const { isPremium, loggedInUser } = useLoggedInUser();
  const { showToast, removeToast } = useToast();
  const [hasSubscriptionConfirmationToastBeenShown, setHasSubscriptionConfirmationToastBeenShown] = useLocalStorage(
    STORAGE.HAS_SUBSCRIPTION_CONFIRMATION_TOAST_BEEN_SHOWN,
    false,
  );
  const router = useRouter();
  const { redirectToStore } = useRedirectToStore();

  useEffect(() => {
    const timeout = 15000;
    const toastId = 'thank-you-for-subscribing';
    const email = loggedInUser ? loggedInUser?.email : '';
    if (isPremium && !hasSubscriptionConfirmationToastBeenShown) {
      const { clearTimeout } = showToast({
        id: toastId,
        title: t('thankYouForSubscribing'),
        subtitle: t('weSentConfirmationEmailtoAddress', {
          emailAddress: email,
        }),
        image: '/images/plane.svg',
        backgroundColor: ToucanColors.green[100],
        titleColor: ToucanColors.gray[600],
        subtitleColor: ToucanColors.gray[600],
        boldSubtitle: false,
        onClick: () => {
          setHasSubscriptionConfirmationToastBeenShown(true);
          removeToast(toastId);
          clearTimeout();
        },
        onClose: () => {
          setHasSubscriptionConfirmationToastBeenShown(true);
          removeToast(toastId);
          clearTimeout();
        },
        onTimeout: () => {
          setHasSubscriptionConfirmationToastBeenShown(true);
          removeToast(toastId);
        },
        timeout,
      });
    }
  }, [isPremium, hasSubscriptionConfirmationToastBeenShown]);

  useEffect(() => {
    if (isInstalled) {
      router.push('/dashboard');
      return;
    }

    if (!isExtensionStatusLoading) {
      trackEvent({
        name: Events.DownloadExtensionPromptSeen,
        properties: {},
      });
    }
  }, [isInstalled, isExtensionStatusLoading]);

  const navbarHeight = '63px';

  if (isExtensionStatusLoading || isInstalled) {
    return null;
  }

  return (
    <>
      <Global
        styles={css`
          body {
            background: url(/images/download-prompt/bg.svg);
            background-size: auto;
            background-repeat: no-repeat;
            ${theme.breakpoints.up('md')} {
              background-size: contain;
            }
          }
        `}
      />

      <Layout hasFooter={false} isNavbarRightContentVisible={false} isContained={false} areLinksHidden>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          css={css`
            height: calc(100vh - ${navbarHeight});
          `}
        >
          <Box m={2} maxWidth="475px" textAlign="center">
            <img
              src="/images/download-prompt/toucan-wave-behind-browser.svg"
              alt="Toucan"
              width="224px"
              height="219px"
            />

            <Typography variant="lg" font="secondary" color={textLevel1}>
              {t('almostDone')}
            </Typography>
            <Box mt={2}>
              <Typography variant="md" color={textLevel1}>
                {t('onlyThingLeftToDoNowIsInstallToucan')}
              </Typography>
            </Box>
            <Box mt={4} width="100%">
              <Button
                size="medium"
                target="_self"
                fullWidth
                onClick={() => {
                  trackEvent({
                    name: Events.DownloadExtensionPromptClicked,
                    properties: {},
                  });
                  redirectToStore();
                }}
              >
                {t('installToucan')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Layout>
    </>
  );
};

export default DownloadPrompt;

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  return {
    props: {
      ...(await getTranslations({ locale, namespaces: 'download-prompt' })),
    },
  };
};
